// Copyright (c) Ping. All rights reserved.
//
// Renders the on-demand per-pane settings guide. It rebuilds its steps for
// whichever pane was requested and force-starts (manual mode, so it never
// auto-runs and never marks itself seen). Mount once inside the shell.

import { useEffect, useMemo } from 'react';

import { useRouter } from '@shell/RouterContext';

import { GuidedTourHost } from './GuidedTour';
import { buildPaneGuideSteps } from './paneGuideSteps';
import { usePaneGuideStore } from './paneGuideStore';
import { TOUR_PANE_GUIDE } from './tourIds';
import { useTourReplay } from './tourReplayStore';

// Above the auto settings tour so a guide the user explicitly opened wins.
const PANE_GUIDE_PRIORITY = 40;

export function PaneGuideHost(): JSX.Element | null {
  const router = useRouter();
  const modalOpen = router.modal.kind === 'settings';
  const pane = usePaneGuideStore((s) => s.pane);
  const nonce = usePaneGuideStore((s) => s.nonce);

  const steps = useMemo(() => (pane !== null ? buildPaneGuideSteps(pane) : []), [pane]);

  // Launch (or relaunch) whenever open() bumps the nonce. Steps are already
  // rebuilt for the requested pane by the time this effect runs.
  useEffect(() => {
    if (nonce > 0 && pane !== null) {
      useTourReplay.getState().replay(TOUR_PANE_GUIDE);
    }
  }, [nonce, pane]);

  if (pane === null || steps.length === 0) {
    return null;
  }

  return (
    <GuidedTourHost
      tourId={TOUR_PANE_GUIDE}
      steps={steps}
      priority={PANE_GUIDE_PRIORITY}
      enabled={modalOpen}
      manual
    />
  );
}
